import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import t from 'react-translate';
import { NvPillTab } from 'shared/components/nv-pill-tab';
import { dropdownZIndex, halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { teal } from 'styles/global_defaults/colors';
import { getLearnerProgressCounts, deleteSavedView } from 'redux/actions/learner-progress';
import { getCourseExercises } from 'redux/actions/lecture-pages';
import { CourseExercise } from 'redux/schemas/models/exercise';
import { LearnerProgressSavedView } from 'redux/schemas/models/learner-progress-saved-view';
import { RootState, CombinedCourse } from 'redux/schemas';
import { getCurrentCourse, getCourseAliases } from 'redux/selectors/course';
import { Course } from 'redux/schemas/models/course';
import { useAppDispatch } from 'redux/store';
import ClickableContainer from 'components/clickable-container';
import SavedViewPill from './saved-view-pill';
import SkeletonPill from './skeleton-pill-tabs';
import { buildDefaultTabs, buildTabItems } from './learner-progress-tab-items';
import { config } from '@config/pendo.config.json';


export enum TabTypes {
  TOTAL = 'total',
  COMPLETED = 'completed',
  IN_PROGRESS = 'in_progress',
  MISSED_DEADLINE = 'missed_deadline',
  NEVER_ACTIVE = 'never_active',
}
export type LearnerProgressTotals = {
  totalLearners: number,
  completed: number,
  inProgress: number,
  missedDeadlines: number,
  neverActive: number,
};
type LearnerProgressTabsProps = {
  totals: LearnerProgressTotals,
  activeTab: string,
  selectTab: (tab: TabTypes) => void,
  selectSavedView: (viewCode: string) => void,
  onEditSavedViewFilters: (view: LearnerProgressSavedView) => void,
  setActiveTab: (tab: string) => void,
};

const SCROLL_AMOUNT = 200;
const ARROW_SIZE = 36;

const LearnerProgressTabs = (props: LearnerProgressTabsProps) => {
  const carouselStyles = css`
    display: flex;
    align-items: center;
    position: relative;
    z-index: ${dropdownZIndex};
    overflow: hidden;
  `;

  const scrollContainerStyles = css`
    overflow-x: auto;
    scrollbar-width: none;
    margin-right: ${standardSpacing}px;
    &::-webkit-scrollbar {
      display: none;
    }
    flex: 1;
    min-width: 0;
  `;

  const styles = css`
    display: flex;
    gap: ${halfSpacing}px;
    padding: ${halfSpacing}px ${standardSpacing}px;
    align-items: center;
    flex-wrap: nowrap;

    .hover-content {
      padding: ${halfSpacing}px;
    }
  `;

  const scrollArrowStyles = css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
    width: ${ARROW_SIZE}px;
    height: 100%;
    background-color: ${teal}E6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    justify-content: center;
    align-items: center;

    &.scroll-arrow-left {
      left: 0;
    }

    &.scroll-arrow-right {
      right: 0;
    }
  `;

  const dispatch = useAppDispatch();
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const scrollLeft = () => tabsContainerRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  const scrollRight = () => tabsContainerRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });

  const aliases = useSelector((state) => getCourseAliases(state));

  const { activeTab, setActiveTab } = props;
  const currentCourse = useSelector<RootState, CombinedCourse>((state) => getCurrentCourse(state)) as Course;
  const savedViews: LearnerProgressSavedView[] = useSelector((state: RootState) => state.app.learnerProgressDashboard.savedViews);
  const isCountsLoading = useSelector((state: RootState) => state.app.learnerProgressDashboard.isCountsLoading);
  const exercises: CourseExercise[] = useSelector((state: RootState) => state.app.lecturePage.exercises);

  const onSelectTab = (tab: TabTypes) => {
    setActiveTab(tab);
    props.selectTab(tab);
  };

  const onDeleteSavedView = (view: LearnerProgressSavedView, tabId: string) => {
    dispatch(deleteSavedView({ catalogId: currentCourse.catalogId, id: view.id, viewName: view.viewName }));
    if (activeTab === tabId) {
      onSelectTab(TabTypes.TOTAL);
    }
  };

  const tabs = buildDefaultTabs({
    aliases,
    totals: props.totals,
    onSelectTab,
  });

  useEffect(() => {
    dispatch(getLearnerProgressCounts({
      catalogId: currentCourse.catalogId,
    }));
    if (exercises.length === 0) {
      dispatch(getCourseExercises({ catalogId: currentCourse.catalogId }));
    }
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = tabsContainerRef.current;
    el?.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);
    return () => {
      el?.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [tabs.length, savedViews.length, isCountsLoading, checkOverflow]);

  return (
    <div css={carouselStyles}>
      {showLeftArrow && (
        <ClickableContainer
          onClick={scrollLeft}
          className='scroll-arrow scroll-arrow-left'
          css={scrollArrowStyles}
          aria-label={t.LEARNER_PROGRESS.TABS.SCROLL_LEFT()}
        >
          <i className='icon icon-xs-small icon-arrow-left' />
        </ClickableContainer>
      )}
      <div css={scrollContainerStyles} ref={tabsContainerRef}>
        <div css={styles}>
          {buildTabItems(tabs, savedViews).map((item) => {
            if (isCountsLoading) {
              return <SkeletonPill key={item.id} tabId={item.id} />;
            }
            if (item.type === 'savedView') {
              return (
                <SavedViewPill
                  key={item.id}
                  view={item.view}
                  isSelected={activeTab === item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    props.selectSavedView(item.view.viewCode);
                  }}
                  onEditFilters={() => props.onEditSavedViewFilters(item.view)}
                  onDelete={() => onDeleteSavedView(item.view, item.id)}
                  tabId={item.id}
                />
              );
            }
            return (
              <NvPillTab
                key={item.id}
                label={item.text}
                count={item.count}
                isSelected={activeTab === item.id}
                onClick={item.onClick}
                id={item.id}
                dataQA={item.dataQA}
                badgeColor={item.badgeColor}
              />
            );
          })}
        </div>
      </div>
      {showRightArrow && (
        <ClickableContainer
          onClick={scrollRight}
          className='scroll-arrow scroll-arrow-right'
          css={scrollArrowStyles}
          aria-label={t.LEARNER_PROGRESS.TABS.SCROLL_RIGHT()}
        >
          <i className='icon icon-xs-small icon-arrow-right' />
        </ClickableContainer>
      )}
    </div>
  );
};

export default LearnerProgressTabs;
